import { useState } from "react";
import { Eye, EyeOff, Trash2, Upload } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, fetchCollection, formatApiError } from "@/lib/api";

const initial = { name: "", url: "", tagline: "", order: "0" };

export function SponsorsAdmin() {
  const queryClient = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-sponsors"], queryFn: () => fetchCollection("/admin/sponsors") });
  const [form, setForm] = useState(initial);
  const [logo, setLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-sponsors"] });
    queryClient.invalidateQueries({ queryKey: ["sponsors"] });
  };
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data: uploaded } = await api.post("/admin/sponsors/logo", body, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo({ ...uploaded, name: file.name });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
    }
  };

  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/sponsors", { ...form, order: Number(form.order) || 0, url: form.url || null, tagline: form.tagline || null, logo_path: logo.logo_path, active: true })).data,
    onSuccess: () => { toast.success("Patrocinador publicado en portada"); setForm(initial); setLogo(null); refresh(); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const toggle = useMutation({
    mutationFn: async (item) => (await api.put(`/admin/sponsors/${item.id}`, { name: item.name, url: item.url, logo_path: item.logo_path, tagline: item.tagline, order: item.order, active: !item.active })).data,
    onSuccess: refresh,
    onError: (error) => toast.error(formatApiError(error)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/sponsors/${id}`)).data,
    onSuccess: () => { toast.success("Patrocinador eliminado"); refresh(); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const active = data.filter((item) => item.active).length;

  return (
    <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]" data-testid="admin-sponsors-panel">
      <form onSubmit={(event) => { event.preventDefault(); if (!logo) { toast.error("Sube primero el logotipo"); return; } create.mutate(); }} className="admin-form" data-testid="admin-sponsor-form">
        <p className="eyebrow">Logotipos en portada · {active}/4 activos</p>
        <label className={`flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed p-5 text-center ${logo ? "border-clay/60 bg-clay/10" : "border-white/20 hover:border-clay/50"}`}>
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="admin-sponsor-logo-input" />
          {logo ? <img src={`${process.env.REACT_APP_BACKEND_URL}${logo.logo_url}`} alt="Logotipo" className="max-h-20 object-contain" data-testid="admin-sponsor-logo-preview" /> : <Upload className="h-6 w-6 text-clay" />}
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300" data-testid="admin-sponsor-logo-status">{uploading ? "Subiendo..." : logo ? logo.name : "Subir logotipo (PNG, SVG, JPG · máx. 3 MB)"}</span>
        </label>
        <label className="block"><span className="field-label">Nombre del patrocinador</span><Input value={form.name} onChange={(event) => update("name", event.target.value)} required className="h-12 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid="admin-sponsor-name-input" /></label>
        <label className="block"><span className="field-label">Enlace (web del patrocinador)</span><Input type="url" value={form.url} onChange={(event) => update("url", event.target.value)} placeholder="https://" className="h-12 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid="admin-sponsor-url-input" /></label>
        <div className="grid gap-5 md:grid-cols-[1fr_120px]">
          <label className="block"><span className="field-label">Lema corto (opcional)</span><Input value={form.tagline} onChange={(event) => update("tagline", event.target.value)} className="h-12 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid="admin-sponsor-tagline-input" /></label>
          <label className="block"><span className="field-label">Orden</span><Input type="number" value={form.order} onChange={(event) => update("order", event.target.value)} className="h-12 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid="admin-sponsor-order-input" /></label>
        </div>
        <Button disabled={create.isPending || uploading} className="h-12 rounded-none bg-clay font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass" data-testid="admin-sponsor-submit-button">{create.isPending ? "Publicando..." : "Publicar logotipo"}</Button>
      </form>

      <div className="grid gap-3" data-testid="admin-sponsors-list">
        {data.length === 0 ? <p className="border border-dashed border-white/15 p-6 text-sm text-slate-500" data-testid="admin-sponsors-empty">Todavía no hay patrocinadores. Caben hasta 4 logotipos en la portada.</p> : null}
        {data.map((item) => (
          <article key={item.id} className={`grid gap-4 border border-white/10 p-4 sm:grid-cols-[120px_1fr_auto] sm:items-center ${item.active ? "bg-white/[0.03]" : "bg-transparent opacity-60"}`} data-testid={`admin-sponsor-item-${item.id}`}>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${item.logo_url}`} alt={item.name} className="max-h-14 w-full object-contain" />
            <div className="min-w-0">
              <p className="font-bold text-slate-100">{item.name} <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">· orden {item.order}</span></p>
              {item.url ? <a href={item.url} target="_blank" rel="noreferrer" className="truncate text-xs text-slate-400 hover:text-clay">{item.url}</a> : null}
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500" data-testid={`admin-sponsor-status-${item.id}`}>{item.active ? "Visible en portada" : "Oculto"}</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => toggle.mutate(item)} className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.18em]" data-testid={`admin-sponsor-toggle-${item.id}`}>{item.active ? <><EyeOff className="h-3 w-3" /> Ocultar</> : <><Eye className="h-3 w-3" /> Mostrar</>}</Button>
              <Button type="button" variant="outline" onClick={() => window.confirm(`¿Eliminar a ${item.name}?`) && remove.mutate(item.id)} className="rounded-none border-red-500/25 bg-red-500/5 font-mono text-[10px] uppercase tracking-[0.18em] text-red-200 hover:bg-red-500/20" data-testid={`admin-sponsor-delete-${item.id}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
